import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GDLLogo } from './GDLLogo';
import GDLLayout from './GDLLayout';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import './gdl.css';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function GDLLogin() {
  const [mode, setMode] = useState('civil_servant');
  const [phone, setPhone] = useState('08012340000');
  const [pin, setPin] = useState('1234');
  const [email, setEmail] = useState('admin@gdl.com');
  const [password, setPassword] = useState('gdldemo2026');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'civil_servant') {
        const { data } = await axios.post(`${API}/api/demo/gdl/auth/civil-servant`, { phone, pin }, { withCredentials: true });
        localStorage.setItem('gdl_demo_token', data.token);
        localStorage.setItem('gdl_demo_user', JSON.stringify({ ...data.user, full_name: data.user.name || data.user.full_name }));
        nav('/demo/gdl/dashboard');
      } else {
        const { data } = await axios.post(`${API}/api/demo/gdl/auth/admin`, { email, password }, { withCredentials: true });
        localStorage.setItem('gdl_demo_token', data.token);
        localStorage.setItem('gdl_demo_user', JSON.stringify({ ...data.user, full_name: data.user.name || data.user.full_name }));
        nav('/demo/gdl/admin');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', background: '#F5E1E4' }}>
      {/* ── Wine Hero Section ────────────────────── */}
      <div style={{
        flex: '0 0 auto', padding: '48px 28px 44px', position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(165deg, #B03140 0%, #8E2233 50%, #5A1520 100%)',
      }}>
        {/* Decorative circles */}
        <div style={{
          position: 'absolute', top: -40, right: -40, width: 180, height: 180,
          borderRadius: '50%', border: '1px solid rgba(255,255,255,0.08)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: -20, left: -30, width: 120, height: 120,
          borderRadius: '50%', background: 'rgba(255,255,255,0.04)',
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <GDLLogo height={36} showText={false} />
          <h1 style={{
            fontSize: 28, fontWeight: 700, color: '#FFFFFF', lineHeight: 1.15,
            marginTop: 28, marginBottom: 10, letterSpacing: '-0.02em',
          }}>
            Step into<br />your financial<br />future
          </h1>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5, maxWidth: 240 }}>
            Salary. Benefits. Investments.<br />All in one place.
          </p>
        </div>
      </div>

      {/* ── White Form Card ──────────────────────── */}
      <div style={{
        flex: 1, background: '#FFFFFF',
        borderRadius: '28px 28px 0 0', marginTop: -12,
        padding: '28px 24px 24px', position: 'relative', zIndex: 2,
        boxShadow: '0 -4px 30px rgba(176,49,64,0.1)',
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#2D0A10', marginBottom: 4 }}>
          {mode === 'civil_servant' ? 'Welcome back' : 'Admin Portal'}
        </div>
        <div style={{ fontSize: 13, color: '#A07078', marginBottom: 24 }}>
          Sign in to your GDL+ account
        </div>

        <form onSubmit={handleLogin}>
          {mode === 'civil_servant' ? (
            <>
              <label style={labelStyle}>Phone Number</label>
              <input value={phone} onChange={e => setPhone(e.target.value)} style={inputStyle}
                placeholder="08012345678" data-testid="input-phone" />

              <label style={{ ...labelStyle, marginTop: 18 }}>PIN</label>
              <div style={{ position: 'relative' }}>
                <input type={showPass ? 'text' : 'password'} value={pin}
                  onChange={e => setPin(e.target.value)} style={inputStyle}
                  placeholder="4-digit PIN" maxLength={4} data-testid="input-pin" />
                <button type="button" onClick={() => setShowPass(!showPass)} style={{
                  position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                }}>
                  {showPass ? <EyeOff size={16} color="#A07078" /> : <Eye size={16} color="#A07078" />}
                </button>
              </div>
            </>
          ) : (
            <>
              <label style={labelStyle}>Email</label>
              <input value={email} onChange={e => setEmail(e.target.value)} style={inputStyle}
                placeholder="admin@gdl.com" data-testid="input-email" />

              <label style={{ ...labelStyle, marginTop: 18 }}>Password</label>
              <div style={{ position: 'relative' }}>
                <input type={showPass ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)} style={inputStyle}
                  placeholder="Password" data-testid="input-password" />
                <button type="button" onClick={() => setShowPass(!showPass)} style={{
                  position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                }}>
                  {showPass ? <EyeOff size={16} color="#A07078" /> : <Eye size={16} color="#A07078" />}
                </button>
              </div>
            </>
          )}

          {error && (
            <div style={{ color: '#DC2626', fontSize: 13, marginTop: 12, padding: '10px 14px', background: '#FEF2F2', borderRadius: 10 }} data-testid="login-error">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} data-testid="login-submit" style={{
            width: '100%', padding: '15px', marginTop: 24,
            background: loading ? '#D4838C' : '#B03140', color: '#fff',
            border: 'none', borderRadius: 14, fontSize: 15, fontWeight: 600,
            cursor: loading ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            boxShadow: '0 4px 16px rgba(176,49,64,0.25)',
            transition: 'background 0.2s ease',
          }}>
            {loading ? 'Signing in...' : (
              <>{mode === 'civil_servant' ? 'Sign In' : 'Admin Sign In'} <ArrowRight size={16} /></>
            )}
          </button>
        </form>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0 16px' }}>
          <div style={{ flex: 1, height: 1, background: 'rgba(176,49,64,0.08)' }} />
          <span style={{ fontSize: 11, color: '#A07078', textTransform: 'uppercase', letterSpacing: '0.05em' }}>or</span>
          <div style={{ flex: 1, height: 1, background: 'rgba(176,49,64,0.08)' }} />
        </div>

        <button type="button" onClick={() => { setMode(m => m === 'civil_servant' ? 'admin' : 'civil_servant'); setError(''); }}
          data-testid="toggle-mode"
          style={{
            width: '100%', padding: '13px', background: 'transparent', color: '#8E2233',
            border: '1.5px solid rgba(176,49,64,0.15)', borderRadius: 14, fontSize: 13, fontWeight: 600,
            cursor: 'pointer', transition: 'all 0.15s ease',
          }}>
          {mode === 'civil_servant' ? 'Admin Login' : 'Civil Servant Login'}
        </button>

        <button type="button" onClick={() => nav('/demo/gdl/onboard')}
          data-testid="new-account-link"
          style={{
            display: 'block', width: '100%', textAlign: 'center', marginTop: 16,
            background: 'none', border: 'none', color: '#B03140', fontSize: 13,
            fontWeight: 600, cursor: 'pointer',
          }}>
          New to GDL+? Create an account
        </button>
      </div>
    </div>
  );

  return <GDLLayout showNav={false}>{content}</GDLLayout>;
}

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600, color: '#8E2233',
  marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em',
};
const inputStyle = {
  width: '100%', padding: '14px 16px', border: '1.5px solid rgba(176,49,64,0.12)', borderRadius: 12,
  fontSize: 15, outline: 'none', boxSizing: 'border-box', color: '#2D0A10',
  transition: 'border-color 0.2s, box-shadow 0.2s', background: 'rgba(176,49,64,0.03)',
};
